import React from 'react';
import { Loader2, Briefcase, Plus, ArrowRight, Phone, Trash2, Repeat, Settings as SettingsIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { moduleApi } from '../../lib/api';
import { Button } from '../ui/Button';
import { useConfirm } from '../ui/ConfirmProvider';
import { PIPELINE_STAGE_LABELS_PRESTATION, PipelineStagePrestation, isTerminalStagePrestation } from './pipelineStagesPrestation';
import { NouveauDossierPrestationModal } from './NouveauDossierPrestationModal';
import { ReglagesDossiers } from './ReglagesDossiers';
import { useAuth } from '../../context/AuthContext';
import { useMoiCommerciaux } from '../Commerciaux/useMoiCommerciaux';

interface DossierRow {
    dossier_id: string;
    label: string;
    type: string;
    email: string;
    phone: string;
    stage: PipelineStagePrestation;
    stage_since?: string | null;
    devis_signed_received_at?: string | null;
    closed_at?: string | null;
    lost_at?: string | null;
    lost_reason: string;
    appointment_id?: string | null;
    appointment_status?: string | null;
    appointment_date?: string | null;
    appointment_start_time?: string | null;
}

// Ce qu'il y a à faire sur un dossier, aujourd'hui. Les dossiers qui attendent le client (signature, prestation à venir)
// n'ont pas d'action : ils n'apparaissent pas dans « À faire ».
type ActionDossier = 'etudier' | 'devis' | 'relancer' | 'rdv' | 'cloturer';
const ACTIONS: { cle: ActionDossier; titre: string; aide: string }[] = [
    { cle: 'etudier', titre: 'Demandes à étudier', aide: 'Reçues du site, personne ne les a lues' },
    { cle: 'devis', titre: 'Devis à établir', aide: 'Dossier ouvert, devis pas encore envoyé' },
    { cle: 'relancer', titre: 'Devis à relancer', aide: 'Sans réponse depuis 5 jours ou plus' },
    { cle: 'rdv', titre: 'Rendez-vous à réserver', aide: 'Devis signé, il reste à planifier' },
    { cle: 'cloturer', titre: 'À clôturer', aide: 'Prestation faite ou date passée' },
];
const JOUR_MS = 86400000;
function actionDe(r: { stage: string; stage_since?: string | null; appointment_date?: string | null }): ActionDossier | null {
    if (r.stage === 'demande_recue') return 'etudier';
    if (r.stage === 'dossier_cree') return 'devis';
    if (r.stage === 'devis_envoye') {
        const depuis = r.stage_since ? new Date(r.stage_since).getTime() : NaN;
        return Number.isFinite(depuis) && Date.now() - depuis >= 5 * JOUR_MS ? 'relancer' : null;
    }
    if (r.stage === 'devis_signe') return 'rdv';
    if (r.stage === 'prestation_realisee') return 'cloturer';
    if (r.stage === 'rdv_reserve' && r.appointment_date && new Date(r.appointment_date).getTime() + JOUR_MS < Date.now()) return 'cloturer';
    return null;
}

const fmtDate = (d?: string | null) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; }
};

/**
 * Écran d'accueil du module « Suivi de prestation » (Prestataire) — le pipeline commercial, du coup de
 * téléphone jusqu'à la clôture. Même esprit que SuiviPrivatisationModule.tsx côté Restauration, mais table
 * et code entièrement propres à ce module.
 */
export function PrestationDossierModule() {
    const navigate = useNavigate();
    // Un commercial n'ouvre jamais un dossier à la main : il passe toujours par une estimation, puis « Établir officiellement le devis ».
    const { user } = useAuth();
    const commercial = useMoiCommerciaux(user?.type === 'collaborator').moi?.role === 'commercial';
    const [rows, setRows] = React.useState<DossierRow[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [erreur, setErreur] = React.useState('');
    const [filtre, setFiltre] = React.useState<'en_cours' | 'toutes'>('en_cours');
    // Filtre Pro/Particulier (2026-09-05, demande d'Emeni) — même vocabulaire que le reste de l'app
    // (client_type_scope) : 'tous' n'exclut rien, les deux autres valeurs comparent r.type tel quel.
    const [filtreType, setFiltreType] = React.useState<'tous' | 'PROFESSIONNEL' | 'PRIVÉ'>('tous');
    const [ouvertureDossier, setOuvertureDossier] = React.useState(false);
    const [reglagesOuverts, setReglagesOuverts] = React.useState(false);
    const [filtreAction, setFiltreAction] = React.useState<ActionDossier | null>(null);
    const [passagesEnRetard, setPassagesEnRetard] = React.useState(0);
    const [passagesAVenir, setPassagesAVenir] = React.useState(0);
    const confirmAction = useConfirm();

    const supprimerDossier = async (r: DossierRow) => {
        if (!(await confirmAction({
            title: 'Supprimer ce dossier ?',
            description: <><strong>{r.label}</strong> et ses devis en brouillon seront effacés. La fiche du client reste dans votre CRM.</>,
            confirmLabel: 'Supprimer le dossier', destructive: true,
        }))) return;
        try {
            const res: any = await moduleApi.deletePrestationDossier(r.dossier_id);
            if (res?.error) { setErreur(res.error); return; }
            setErreur('');
            charger();
        } catch (e: any) { setErreur(e?.message || 'Suppression impossible.'); }
    };

    const charger = React.useCallback(() => {
        setLoading(true);
        moduleApi.getPrestationDossiers()
            .then((r: any) => setRows(Array.isArray(r) ? r : []))
            .catch((e: any) => setErreur(e?.message || 'Erreur de chargement'))
            .finally(() => setLoading(false));
    }, []);

    React.useEffect(() => { charger(); }, [charger]);

    // Contrats annuels actifs dont le prochain passage n'est pas planifié et dépasse sa date cible de 14 jours.
    React.useEffect(() => {
        moduleApi.getSubscriptions().then((subs: any) => {
            const liste = Array.isArray(subs) ? subs : [];
            // Date cible du prochain passage non planifié : les passages sont répartis à égale distance sur la durée du contrat.
            const cibles: number[] = [];
            liste.forEach((x: any) => {
                const total = Number(x.occurrences_total) || 0;
                const faits = Number(x.occurrences_used) || 0;
                if (x.status !== 'actif' || !total || Number(x.passages_libres) || x.next_appointment_date || faits >= total) return;
                const debut = new Date(x.start_date).getTime();
                const fin = new Date(x.end_date).getTime();
                if (!Number.isFinite(debut) || !Number.isFinite(fin)) return;
                cibles.push(debut + (faits * (fin - debut)) / total);
            });
            const maintenant = Date.now();
            setPassagesEnRetard(cibles.filter((t) => t + 14 * JOUR_MS < maintenant).length);
            // À venir : la date cible tombe dans les 30 prochains jours (ou vient de passer, sans être encore en retard).
            setPassagesAVenir(cibles.filter((t) => t + 14 * JOUR_MS >= maintenant && t <= maintenant + 30 * JOUR_MS).length);
        }).catch(() => { setPassagesEnRetard(0); setPassagesAVenir(0); });
    }, []);

    const ouverts = rows.filter((r) => !isTerminalStagePrestation(r.stage) && r.stage !== 'cloturee');
    const compte = (cle: ActionDossier) => ouverts.filter((r) => actionDe(r) === cle).length;

    const visibles = rows
        .filter((r) => filtre === 'toutes' || !isTerminalStagePrestation(r.stage) && r.stage !== 'cloturee')
        .filter((r) => filtreType === 'tous' || (filtreType === 'PROFESSIONNEL' ? r.type === 'PROFESSIONNEL' : r.type !== 'PROFESSIONNEL'))
        .filter((r) => !filtreAction || actionDe(r) === filtreAction);

    return (
        <div className="p-6 max-w-6xl mx-auto space-y-5">
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                    <Briefcase size={22} className="text-brand-600 dark:text-brand-400" />
                    <h1 className="text-xl font-black text-content">Dossiers clients</h1>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {!commercial && (
                        <button type="button" onClick={() => setReglagesOuverts(true)} title="Régler les dossiers : façon de travailler, délais, e-mails du devis, signature"
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-surface border border-border text-content-muted hover:text-content hover:border-brand-500 transition-colors">
                            <SettingsIcon size={14} /> Réglages
                        </button>
                    )}
                    {commercial
                        ? <Button onClick={() => navigate('/estimation-devis')}><Plus size={15} /> Nouvelle estimation</Button>
                        : <Button onClick={() => setOuvertureDossier(true)}><Plus size={15} /> Nouveau dossier</Button>}
                </div>
            </div>
            <p className="text-sm text-content-muted -mt-2">Un dossier suit un client de sa demande jusqu'à la prestation réalisée : devis, signature, rendez-vous.</p>

            {!loading && (
                <section aria-label="À faire aujourd'hui" className="space-y-2">
                    <h2 className="text-[11px] font-black uppercase tracking-widest text-content-muted">À faire aujourd'hui</h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5">
                        {ACTIONS.map((a) => {
                            const n = compte(a.cle);
                            const actif = filtreAction === a.cle;
                            return (
                                <button key={a.cle} type="button" disabled={n === 0} onClick={() => setFiltreAction(actif ? null : a.cle)} title={a.aide}
                                    className={'text-left p-3 rounded-xl border transition-colors ' + (actif ? 'border-brand-500 bg-brand-500/10' : n > 0 ? 'border-border bg-surface hover:border-brand-500/50' : 'border-border bg-surface-soft opacity-60')}>
                                    <p className={'text-2xl font-black ' + (n > 0 ? 'text-content' : 'text-content-muted')}>{n}</p>
                                    <p className="text-[11px] font-bold text-content-muted leading-tight">{a.titre}</p>
                                </button>
                            );
                        })}
                        <button type="button" disabled={passagesAVenir === 0} onClick={() => navigate('/abonnements')} title="Contrats annuels dont un passage est à planifier dans les 30 prochains jours"
                            className={'text-left p-3 rounded-xl border transition-colors ' + (passagesAVenir > 0 ? 'border-amber-500/40 bg-amber-500/5 hover:border-amber-500/70' : 'border-border bg-surface-soft opacity-60')}>
                            <p className={'text-2xl font-black ' + (passagesAVenir > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-content-muted')}>{passagesAVenir}</p>
                            <p className="text-[11px] font-bold text-content-muted leading-tight flex items-center gap-1"><Repeat size={11} /> Passages à planifier (30 jours)</p>
                        </button>
                        <button type="button" disabled={passagesEnRetard === 0} onClick={() => navigate('/abonnements')} title="Contrats annuels dont un passage n'est pas planifié à temps"
                            className={'text-left p-3 rounded-xl border transition-colors ' + (passagesEnRetard > 0 ? 'border-red-500/30 bg-red-500/5 hover:border-red-500/60' : 'border-border bg-surface-soft opacity-60')}>
                            <p className={'text-2xl font-black ' + (passagesEnRetard > 0 ? 'text-red-500' : 'text-content-muted')}>{passagesEnRetard}</p>
                            <p className="text-[11px] font-bold text-content-muted leading-tight flex items-center gap-1"><Repeat size={11} /> Passages de contrat en retard</p>
                        </button>
                    </div>
                    {filtreAction && (
                        <p className="text-xs text-content-muted">Filtré sur « {ACTIONS.find((a) => a.cle === filtreAction)?.titre} ». <button type="button" className="font-bold text-brand-600 dark:text-brand-400 hover:underline" onClick={() => setFiltreAction(null)}>Tout afficher</button></p>
                    )}
                </section>
            )}

            <div className="flex flex-wrap items-center gap-3">
                <div className="flex gap-2">
                    {(['en_cours', 'toutes'] as const).map((f) => (
                        <button key={f} type="button" onClick={() => setFiltre(f)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${filtre === f ? 'border-brand-500 bg-brand-500/10 text-brand-600 dark:text-brand-400' : 'border-border text-content-muted hover:text-content'}`}>
                            {f === 'en_cours' ? 'En cours' : 'Toutes'}
                        </button>
                    ))}
                </div>
                <div className="w-px h-5 bg-border" />
                <div className="flex gap-2">
                    {([['tous', 'Tous'], ['PROFESSIONNEL', 'Pro'], ['PRIVÉ', 'Particulier']] as const).map(([value, label]) => (
                        <button key={value} type="button" onClick={() => setFiltreType(value)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${filtreType === value ? 'border-brand-500 bg-brand-500/10 text-brand-600 dark:text-brand-400' : 'border-border text-content-muted hover:text-content'}`}>
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            {erreur && <p className="text-sm text-red-500 font-semibold">{erreur}</p>}

            {loading ? (
                <div className="flex justify-center py-16"><Loader2 size={26} className="animate-spin text-content-muted" /></div>
            ) : visibles.length === 0 ? (
                <div className="text-center py-16 text-content-muted">
                    <Phone size={28} className="mx-auto mb-2 opacity-40" />
                    <p className="text-sm">Aucun dossier pour le moment.</p>
                </div>
            ) : (
                <div className="border border-border rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-surface-soft text-[10px] font-black uppercase tracking-widest text-content-muted">
                            <tr>
                                <th className="text-left px-4 py-3">Client</th>
                                <th className="text-left px-4 py-3">Type</th>
                                <th className="text-left px-4 py-3">Étape</th>
                                <th className="text-left px-4 py-3">Depuis</th>
                                <th className="text-left px-4 py-3">Rendez-vous</th>
                                <th className="px-4 py-3" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {visibles.map((r) => {
                                const perdu = r.stage === 'perdue' || r.stage === 'annulee';
                                const aTraiter = !!r.devis_signed_received_at && r.stage === 'devis_envoye';
                                return (
                                    <tr key={r.dossier_id} className="hover:bg-surface-soft cursor-pointer transition-colors" onClick={() => navigate(`/suivi-prestation/${r.dossier_id}/pipeline`)}>
                                        <td className="px-4 py-3 font-bold text-content">{r.label}</td>
                                        <td className="px-4 py-3 text-content-muted">{r.type === 'PROFESSIONNEL' ? 'Pro' : 'Particulier'}</td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-bold ${perdu ? 'bg-red-500/10 text-red-400' : r.stage === 'cloturee' ? 'bg-green-500/10 text-green-500' : 'bg-brand-500/10 text-brand-600 dark:text-brand-400'}`}>
                                                {aTraiter && <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />}
                                                {PIPELINE_STAGE_LABELS_PRESTATION[r.stage] || r.stage}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-content-muted">{fmtDate(r.stage_since)}</td>
                                        <td className="px-4 py-3 text-content-muted">{r.appointment_date ? `${fmtDate(r.appointment_date)} ${r.appointment_start_time || ''}` : '—'}</td>
                                        <td className="px-4 py-3 text-right">
                                            <span className="inline-flex items-center gap-3">
                                                <button type="button" title="Supprimer le dossier" onClick={(e) => { e.stopPropagation(); supprimerDossier(r); }}
                                                    className="text-content-muted hover:text-red-500"><Trash2 size={14} /></button>
                                                <ArrowRight size={15} className="text-content-muted" />
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {reglagesOuverts && <ReglagesDossiers onClose={() => setReglagesOuverts(false)} />}

            {ouvertureDossier && (
                <NouveauDossierPrestationModal
                    onClose={() => setOuvertureDossier(false)}
                    onCree={(id) => { setOuvertureDossier(false); navigate(`/suivi-prestation/${id}/pipeline`); }}
                />
            )}
        </div>
    );
}
